// src/oura/screens/SearchPerformance.tsx
//
// Real search performance from Search Console (ourteam-backend#3818), and the
// site's sessions from Google Analytics, on one staff screen.
//
// Everything here is a metric Google returned or arithmetic over it. Nothing is
// generated, and nothing is estimated, which is why this screen is allowed to
// state figures plainly instead of hedging them.
//
// Three things about the Search Console payload the UI has to respect,
// because getting any of them wrong re-introduces exactly the dishonesty
// #3818 removed:
//
//   1. `searchScore` is null, never 0, when GSC is not connected. A zero would
//      read as measured-and-terrible. Null means "we did not measure this".
//   2. Deltas are null on a first scan. "No earlier period", not "0%": flat
//      and unmeasured are different claims.
//   3. `buckets` counts NON-BRANDED searches and the bands are CUMULATIVE
//      (see RankCard).
//
// Off the sidebar: reached from the Google tiles on Overview and Visibility.

import { Link } from "react-router-dom";
import { ActivityIcon, AlertTriangleIcon, BotIcon, ChevronLeftIcon, GlobeIcon, MailIcon, PlugZapIcon, SearchIcon, TrendingUpIcon } from "lucide-react";
import type { AeoSearchPerformance, AeoSearchQuestions } from "@service/aeo";
import type { BusinessAudit } from "../audit";
import type { DataConnector } from "../connectors";
import type { QuestionResult } from "../contract";
import { ouraHref, type View } from "../nav";
import { EmptyState } from "../primitives/EmptyState";
import { hasSearchRows, type GscState } from "../gscState";
import { useStageRefresh } from "../hooks/useRefreshJob";
import { SEARCH_REFRESH, STAGE_RERUN_LIVE } from "../refreshJobs";
import { Bento, FOCUS_RING, FoldScope, PageHead, Surface, TYPE, Tile, Tiles, useFoldOpener } from "@oura/ui";
import { cn } from "../lib/cn";
import { ScreenSentence } from "../components/client/ScreenSentence";
import { aiVisits, num, position, searchesForWhatYouSell } from "../components/search/format";
import { PctChange } from "../components/search/PctChange";
import { RankCard } from "../components/search/RankCard";
import { AllSearchesCard, TopSearchesCard } from "../components/search/KeywordCards";
import { PagesCard } from "../components/search/PagesCard";
import { SearchesAboutYou } from "../components/search/SearchesAboutYou";
import { MonthStory } from "../components/search/MonthStory";
import { CloseToPageOneCard, MoversCard } from "../components/search/SearchMovement";
import { LandingPagesCard, SourcesCard, Versus } from "../components/search/TrafficSources";
import { TrafficCard } from "../components/search/TrafficCard";
import { MonthlyReportPanel } from "../components/search/MonthlyReport";

const SCOPE = "staff-search";

/** A Search Console total's change, or why there is none. */
function totalChange(fraction: number | null | undefined, days: number) {
    return fraction === null || fraction === undefined
        ? "No earlier period"
        : <PctChange percent={fraction * 100} days={days} />;
}

export function SearchPerformance({
    search,
    businessName,
    gscState,
    searchQuestions,
    questions,
    traffic,
    trafficEmpty = null,
    ga4Connector,
    capturedAt,
    pastScan,
    assessmentId,
    businessId,
    isStaff,
    onRefreshed,
    onNavigate,
}: {
    /** `undefined` on a run finalized before #3818. */
    search: AeoSearchPerformance | undefined;
    businessName: string;
    /**
     * The reconciled connection state (gscState.ts). `connected: true` with
     * nothing behind it used to render this entire screen as zeros, which
     * reads as "you rank for nothing", a measurement we never made. Undefined
     * on a fixture business, where there is no catalog to reconcile against.
     */
    gscState?: GscState;
    /**
     * What customers search ABOUT this business, and whether the site answers
     * it (#3879, ourteam-backend#4981). Absent or `{connected: false}` renders
     * no card at all.
     */
    searchQuestions?: AeoSearchQuestions | null;
    /**
     * The 50 questions, so a covered search can open the fix the Visibility
     * table already opens for it. Absent means no Fix column.
     */
    questions?: QuestionResult[];
    /** GA4 sessions (the Traffic card). Absent when no analytics connector supplied it. */
    traffic?: BusinessAudit["traffic"];
    /** GA4 read and recorded no sessions in this window (ourteam-backend#6749). */
    trafficEmpty?: BusinessAudit["trafficEmpty"];
    /** The GA4 connector, so absent traffic can say why. See TrafficCard. */
    ga4Connector?: DataConnector | null;
    /** The scan's capture date, for the traffic chart's span. */
    capturedAt?: string | null;
    /** The scan these figures came from, for the Search Console and Analytics pulls (#3907). */
    assessmentId?: string;
    businessId?: string;
    isStaff?: boolean;
    onRefreshed?: () => void | Promise<void>;
    /** To the Connectors page, from the traffic card's connection states. */
    onNavigate?: (v: View) => void;
    /**
     * A past scan is on screen, and THE SEARCH CONSOLE FIGURES ARE NOT FROM IT
     * (#3933).
     *
     * `results.searchPerformance` is attached at read time from Search Console
     * as it stands today, so opening a scan from three weeks ago shows this
     * week's rankings under that scan's date. Every other card on the
     * workspace is historical; these cannot be, and saying so is the only
     * honest option short of hiding them.
     */
    pastScan?: boolean;
}) {
    // Opens a card from a link elsewhere on the page ("See all", the
    // sessions tile) in place: it keeps its state, such as the traffic range.
    const openCard = useFoldOpener(SCOPE);
    const searchJob = useStageRefresh({
        spec: SEARCH_REFRESH,
        assessmentId,
        businessId: businessId ?? "none",
        onSettled: () => onRefreshed?.(),
    });
    const canPullSearch = STAGE_RERUN_LIVE && !!isStaff && !!assessmentId && !searchJob.absent;

    // Said plainly, because every other card on a past scan is historical and
    // the Search Console ones are not.
    const pastNote = pastScan && (
        <span className="mt-1 block text-sm font-medium text-amber-700">
            {isStaff ? "The Search Console figures" : "The Google figures"} are today&rsquo;s, not from the scan you are viewing.
        </span>
    );
    // The client reaches this screen from the Google tile on Visibility, with
    // no business chrome above it, so it carries its own title, and the way
    // back: the screen is off the client nav, so nothing else leads back.
    const clientHead = isStaff ? null : (
        <PageHead
            over={(
                <Link
                    to={ouraHref("questions", { role: "oura-client" })}
                    className={cn(TYPE.lab, "inline-flex items-center gap-0.5 rounded hover:text-gray-900", FOCUS_RING)}
                >
                    <ChevronLeftIcon aria-hidden size={14} className="shrink-0" />
                    Back to Visibility
                </Link>
            )}
            title="Search performance"
        />
    );
    // "No sessions recorded" only while the connector reads connected now (Jack, #6749):
    // action_needed and a connector list not loaded yet keep their own words.
    const confirmedTrafficEmpty = ga4Connector?.status === "connected" ? trafficEmpty : null;
    const trafficCardEl = (
        <TrafficCard
            traffic={traffic}
            empty={confirmedTrafficEmpty}
            ga4Connector={ga4Connector}
            capturedAt={capturedAt}
            assessmentId={assessmentId}
            businessId={businessId}
            isStaff={isStaff}
            onRefreshed={onRefreshed}
            onNavigate={onNavigate}
        />
    );

    // Staff only: the client reads the sent copy on Reports.
    const reportPanel = isStaff && businessId && <MonthlyReportPanel key={businessId} businessId={businessId} businessName={businessName} />;

    // Nothing to show, for one of several different reasons, and WHICH reason
    // is the whole point. Three cases used to collapse into "isn't connected":
    // never connected, connected with nothing pulled, and connected with rows
    // that matched no page we crawled. The last two are a connection the
    // operator believes is working, so telling them to connect it is both
    // wrong and unactionable. gscState names the cause; this only renders it.
    // Analytics is its own connection, so its card stays either way.
    if (!search || !search.connected || !hasSearchRows(search)) {
        // The scan's own verdict, used when the connector catalog has not been
        // loaded on this screen. `connected: true` with nothing behind it is
        // still not a reason to tell someone to connect what they connected.
        const pulledNothing = !!search?.connected;
        const reconciled = gscState && gscState.kind !== "not_connected" ? gscState : null;
        const title = !isStaff
            ? "No Google figures yet"
            : reconciled?.label ?? (pulledNothing ? "Connected, no pages matched" : "Search Console isn't connected");
        const description = !isStaff
            ? "Your Google search figures show here once Google has figures for your site."
            : reconciled?.detail
            || (pulledNothing
                ? "Search Console is connected, and it returned no rows for any page Oura crawled. The two causes worth checking are a property that covers a different host or protocol, and a site too new to have collected impressions."
                : `Connect Google Search Console to see which searches ${businessName} appears for, where it ranks, and how that is moving. Nothing here is estimated, it all comes straight from Google.`);
        return (
            <FoldScope value={SCOPE}>
                {clientHead}
                <ScreenSentence title="Search performance">
                    {isStaff ? "No Search Console figures to show." : "No Google figures to show yet."}{pastNote}
                </ScreenSentence>
                <div className="space-y-5">
                    {reportPanel}
                    <Surface>
                        <EmptyState
                            icon={gscState?.tone === "warn" || pulledNothing ? AlertTriangleIcon : PlugZapIcon}
                            title={title}
                            description={description}
                        />
                    </Surface>
                    {trafficCardEl}
                </div>
            </FoldScope>
        );
    }

    const totals = search.totals;
    const days = search.window?.days;
    // The window a change is measured against: the previous one when the
    // payload names it, else the same length as the current one.
    const priorDays = search.previousWindow?.days ?? days ?? 28;
    const keywords = search.keywords ?? [];
    // GA4 insight blocks (ourteam-backend#6426): each tile and card shows only
    // when its block arrived, so an older payload renders as it did before.
    const ai = aiVisits(traffic);
    const enquiries = traffic?.keyEvents;
    const extraTiles = (ai ? 1 : 0) + (enquiries ? 1 : 0);

    return (
        <FoldScope value={SCOPE}>
            {clientHead}
            <div className="space-y-5">
                {reportPanel}
                <MonthStory search={search} visits={traffic?.visits} visitsDeltaPct={traffic?.deltaPct} note={pastNote} />
                <Tiles cols={extraTiles ? 3 : 4}>
                    <Tile
                        label="Clicks"
                        icon={{ icon: SearchIcon, area: "google" }}
                        value={num(totals?.clicks)}
                        sub={totalChange(totals?.clicksDeltaPct, priorDays)}
                    />
                    <Tile
                        label={isStaff ? "Impressions" : "Times shown on Google"}
                        icon={{ icon: GlobeIcon, area: "google" }}
                        value={num(totals?.impressions)}
                        sub={totalChange(totals?.impressionsDeltaPct, priorDays)}
                    />
                    <Tile
                        label="Average position"
                        icon={{ icon: TrendingUpIcon, area: "google" }}
                        value={position(totals?.avgPosition)}
                        sub={days !== undefined ? `${days} days` : isStaff ? "Search Console" : "On Google"}
                    />
                    <Tile
                        // Not "visits from Google": every visit, from any source,
                        // which is why it is bigger than the clicks beside it.
                        label={isStaff ? "Sessions, GA4" : "All visits to your site"}
                        icon={{ icon: ActivityIcon, area: "google" }}
                        value={num(traffic?.visits)}
                        sub={traffic
                            ? traffic.deltaPct !== null
                                ? <PctChange percent={traffic.deltaPct} days={traffic.window?.days ?? 28} />
                                : `${traffic.window?.days ?? 28} days`
                            // No traffic block is unknown, not zero: the client
                            // reads what the visits card says (Jack, #4232).
                            : confirmedTrafficEmpty
                                ? isStaff ? `No sessions in ${confirmedTrafficEmpty.days} days` : `No visits recorded in ${confirmedTrafficEmpty.days} days`
                            : ga4Connector?.status === "connected"
                                ? isStaff ? "No sessions in 28 days" : "No visit figures for this scan"
                                : ga4Connector?.status === "action_needed" ? "Needs attention" : "Not connected"}
                        onClick={() => openCard("traffic")}
                        ariaLabel={isStaff ? "Sessions from Google Analytics: open the traffic card" : "All visits to your site: open the visits card"}
                    />
                {ai && (
                        <Tile
                            label={isStaff ? "AI referrals, GA4" : "Visits from AI assistants"}
                            icon={{ icon: BotIcon, area: "google" }}
                            value={num(ai.now)}
                            sub={ai.before !== null && ai.now !== null && ai.before > 0
                                ? <span><Versus now={ai.now} before={ai.before} /> on the 28 days before</span>
                                : ai.before !== null ? `${num(ai.before)} in the 28 days before` : "28 days"}
                        />
                    )}
                    {enquiries && (
                        <Tile
                            label={isStaff ? "Key events, GA4" : "Enquiries and bookings"}
                            icon={{ icon: MailIcon, area: "google" }}
                            value={num(enquiries.count)}
                            sub={enquiries.prevCount !== null && enquiries.prevCount > 0
                                ? <span><Versus now={enquiries.count} before={enquiries.prevCount} /> on the 28 days before</span>
                                : "28 days"}
                        />
                    )}
                </Tiles>

                <Bento layout="c2">
                    <RankCard search={search} isStaff={isStaff} />
                    <TopSearchesCard keywords={keywords} days={days} isStaff={isStaff} onShowAll={() => openCard("all-searches")} />
                </Bento>

                <Bento layout="c2">
                    <MoversCard keywords={keywords} isStaff={isStaff} />
                    <CloseToPageOneCard keywords={keywords} isStaff={isStaff} />
                </Bento>

                {trafficCardEl}

                {traffic && (() => {
                    const sources = (traffic.sources || traffic.aiReferrals) && <SourcesCard traffic={traffic} isStaff={isStaff} />;
                    const pages = traffic.landingPages && <LandingPagesCard pages={traffic.landingPages} isStaff={isStaff} />;
                    return sources && pages ? <Bento layout="c2">{sources}{pages}</Bento> : sources || pages || null;
                })()}

                {/* What customers ask about this business, answered or not:
                    those above are searches the site already appears for,
                    these are the searches customers make. */}
                <SearchesAboutYou block={searchQuestions} questions={questions} searchJob={canPullSearch ? searchJob : undefined} />

                <AllSearchesCard keywords={keywords} total={searchesForWhatYouSell(search.buckets)} isStaff={isStaff} />
                <PagesCard search={search} isStaff={isStaff} />
            </div>
        </FoldScope>
    );
}
